import {AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Ofwca} from "../../../shared/interface/ofwca";
import {NgForm} from "@angular/forms";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {ReplaySubject} from "rxjs";
import {OfwcaService} from "../../../shared/services/ofwca.service";
import {FilterService} from "../../../shared/services/filter.service";
import {UserPermissions} from "../../../shared/interface/user-permissions";
import {AuthService} from "../../../shared/services/auth.service";
import {delay, distinctUntilChanged, first, takeUntil, tap} from "rxjs/operators";
import {CountedAndPaginated} from "../../../shared/interface/response/counted-and-paginated";
import {select, Store} from "@ngrx/store";
import {getCurrentRouteState} from "../../../shared/ngrx/selector";
import {RouterStateUrl} from "../../../shared/ngrx/route-serializer";
import {ActivatedRoute, Router} from "@angular/router";
import {StoreRootState} from "../../../shared/ngrx/reducers";
import {Uuid} from "../../../shared/interface/response/uuid";
import {JobResultDialogComponent} from "../../../shared/components/job-result-dialog/job-result-dialog.component";
import {HttpErrorResponse} from "@angular/common/http";
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatDialog} from "@angular/material/dialog";
import {INSURANCE_TYPE_ASSETS} from "../../../shared/services/dict.service";

@Component({
  selector: 'app-ofwcas-missing-documents',
  templateUrl: './ofwcas-missing-documents.component.html',
  styleUrls: ['./ofwcas-missing-documents.component.scss']
})
export class OfwcasMissingDocumentsComponent implements  OnDestroy, AfterViewInit {
  @Input() val = true;
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  initialFilters: any;
  ofwcas: Ofwca[] = [];
  totalData = 0;
  displayedColumns: string[] = ['fullName', 'partner' , 'rau', 'diploma', 'complete_declaration', 'contract_active',
    'certificate_tuw_tuw', 'training_inter_polska', 'complete_data', 'assetsRegional'];
  userPermissions: ReplaySubject<UserPermissions>;

  readonly INSURANCE_TYPE_ASSETS = INSURANCE_TYPE_ASSETS;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private ofwcaService: OfwcaService,
    private activatedRoute: ActivatedRoute,
    private filterService: FilterService,
    private store: Store<StoreRootState>,
    private auth: AuthService,
    private router: Router,
    private snack: MatSnackBar,
    private dialog: MatDialog,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.ofwcaService.getOfwcaMissingDocumentsSubject()
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<Ofwca>) => {
          this.ofwcas = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      recordId: 'string',
      eq$profitNumber: 'string',
      contains$firstName: 'string',
      contains$secondName: 'string',
      contains$lastName: 'string',
      contains$pesel: 'string',
      contains$documentNumber: 'string',
      contains$email: 'string',
      contains$phone: 'string',
      address: 'string',
      state: 'int',
      assetsRegional: 'string[]',
      lifeRegional: 'string[]',
      partnerAssetsRegional: 'string[]',
      partner: 'string',
      facilityAddress: 'string',
      facilityState: 'int',
      owner: 'int',
      verified: 'int',
      partnerTag: 'int[]',
      rauNumber: 'string',
      status: 'int[]',
      active: 'int',
      hiperioAutomaticAccessUpdate: 'int',
      hiperioGrantingAccess: 'int',
      signedContractWithTheEmployee: 'int',
      contains$familyName: 'string',
      hasDiploma: 'int',
      completeDeclarations: 'int',
      missingDocument: 'int',
    }, filters);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ngAfterViewInit(): void {
    this.activatedRoute.data
      .pipe(
        delay(0),
        first()
      )
      .subscribe((data: any) => {
        this.val = this.val ?? data.val ?? null;
        this.store
          .pipe(
            select(getCurrentRouteState),
            delay(0),
            distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
            takeUntil(this.destroyed$),
          )
          .subscribe((state: RouterStateUrl) => {
            this.parseFilters(state.params);
            this.paginator.pageIndex = state.params.page;
            this.paginator.pageSize = state.params.pageSize;
            this.loadListData();
          })
        ;
      });

    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        ...this.filtersForm.value
      })]);
    });
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }

   loadListData(): void {
    this.ofwcaService.loadMissingDocumentsList(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.buildFilters());
  }

  downloadReport(): void {
    this.ofwcaService.downloadMissingDocumentsReport(this.buildFilters())
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  copyMessage(val: string): void{
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = val;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
    this.snack.open('Skopiowano do schowka', '', {duration: 2000});

  }

  buildFilters(): any {
    const filters = {...this.initialFilters};

    filters.missingDocument = this.val;

    return filters;
  }
}
